import { FetchResult, MutationResult } from "@apollo/client";
import React, {useCallback, useEffect, useState} from "react";
import { useParams} from "react-router";
import {useSnackbar} from "notistack";

import GQLChanges from "../utils/graphql-changes";
import { DiaFormRelationships, useDiaForm} from "./dia-form";
import {useNavigate} from "react-router-dom";
import {useBlocker} from "./usePrompt";
import {DiaPromptConfirmation} from "./DiaConfimation";



interface YupSchema {
    validate: (any, Object) => Promise<unknown>
}


export type DiaEditorCreateRedirectFunction<MutationResultType> = (args: CreateRedirectArgs<MutationResultType>) => string;

type CreateRedirectArgs<Type> = {
    data:Type,
    baseUrl:string,
    search:string
};

export type DiaEditorSaveArgs<Type>= {
    values:Type
};

export type DiaEditorCreateArgs<Type> = {
    values:Type
};



type DiaEditorProps<Type,QueryResultType,CreateMutation,SaveMutationType,TransientType> = {
    query:QueryResultType
    save:(args:DiaEditorSaveArgs<Type>) => Promise<FetchResult<SaveMutationType>>,
    saveResult:MutationResult<any>,
    getSaveValue:(result:MutationResult<SaveMutationType>) => Type|null|undefined,
    create:(args:DiaEditorCreateArgs<Type>) => Promise<FetchResult<CreateMutation>>,
    createResult:MutationResult<any>|null,
    blankValues:()=>Type,
    label:string,
    saveGuard?:(values:Type,initialValues:Type)=>Promise<boolean>,
    afterSave?:() => void,
    baseUrl:string,
    permission:string,
    validationSchema?:YupSchema,
    transientValues?:TransientType,
    relationships?:DiaFormRelationships,
    cleanGraphqlVariables?:(any,TransientType)=>any,
    getValue:(query:QueryResultType)=>Type|null|undefined,
    getCreateRedirect:DiaEditorCreateRedirectFunction<MutationResult<CreateMutation>>
    allowRedirectWhen?:(path:string,id:string)=>boolean
};

export function useDiaEditor<Type,QueryType,CreateMutationType,SaveMutationType,TransientType>(
        props:DiaEditorProps<Type,QueryType,CreateMutationType,SaveMutationType,TransientType>
    ): any {
    const [isRedirectAllowed, setIsRedirectAllowed] = useState(false);
    //const [campaign,setCampaign] = useState(false);
    const { id } = useParams();
    const [redirectCallback,setRedirectCallback] = useState(null);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const navigate = useNavigate();
    //const userContext = useUserContext();
    const waiting = props.query.loading || props.saveResult.loading || props.createResult?.loading;
    const isNew = (id === 'new');
    const formSubmit = useCallback(async ({ values, initialValues, hasFailed, hasCompleted,transientValues,setTransient }) => {
        const changes = new GQLChanges(props.relationships);
        const uniqid = new Date().getMilliseconds();
        console.log('[DIAEDITOR]', id,'CHANGES PRE',initialValues,values);
        let variables = changes.compare(initialValues, values);
        if(props.cleanGraphqlVariables) {
            variables = props.cleanGraphqlVariables(variables,transientValues);
        }
        console.log('[DIAEDITOR]', 'CHANGES POST', initialValues, values, variables);
        if (!variables) {
            alert('NO VARIABLES');
            return;
        }
        enqueueSnackbar('Saving '+props.label,{key:uniqid+'_SAVE'});
        if(props.saveGuard) {
            const res = await props.saveGuard(values,initialValues);
            if(!res) {
                return;
            }
        }
        if (!isNew && !values.__cloned) {
            //try {
            props.save({values:variables as Type}).then((data) => {
                //closeSnackbar(uniqid+'_SAVE');
                if (data.errors) {
                    console.log('DIAEDITOR error list');
                    data.errors.forEach((error) => {
                        enqueueSnackbar(error.message,{variant:'error'});
                    });
                } else {
                    console.log('DIAEDITOR success');
                    enqueueSnackbar(props.label+' updated.',{key:uniqid+'_SAVED',variant:'success'});
                }
                hasCompleted();
                if(props.afterSave) {
                    props.afterSave({setTransient:setTransient});
                }
            }).catch((error) => {
                console.log('DIAEDITOR save - CRASH AND BURN',error);
                hasFailed();
                enqueueSnackbar(error.message,{key:'CAMPAIGN_SAVED',variant:'error'});
            })
        } else {
            if ("id" in variables) {
                delete variables?.id;
            }
            if ("auto_upgrade" in variables) {
                delete variables?.auto_upgrade;
            }
            props.create({values:variables as Type}).then((data) => {
                console.log('DIAEDITOR creation');
                //closeSnackbar('CAMPAIGN_SAVE');
                enqueueSnackbar(props.label+' created.',{variant:'success'});
                //setIsRedirectAllowed(true);
                hasCompleted();
                if(props.afterSave) {
                    props.afterSave({setTransient:setTransient});
                }
                const url = props.getCreateRedirect({data:data,baseUrl:props.baseUrl,search:location.search});
                setIsRedirectAllowed(true);
                navigate(url);
            }).catch((e) => {
                console.log('DIAEDITOR creation FAILED', e);
                hasFailed();
                enqueueSnackbar('Unable to create '+props.label,{variant:'error'});
            });
        }
    }, [isNew,props.save,props.create,props.saveGuard]);
    const formFail = useCallback(() => {
        enqueueSnackbar('One or more field has failed validation, please correct any highlighted errors and try again',{variant:'error'});
    }, [isNew]);

    const [blankValues,setBlankValues] = useState(props.blankValues());
    useEffect(()=>{
        setBlankValues(props.blankValues);
    },[props.blankValues]);
    console.log('DIAEDITOR',isNew,id);
    const diaForm = useDiaForm<Type,TransientType>({
        inputValues:isNew?blankValues:props.getValue(props.query),
        onSubmit: formSubmit,
        onFailure: formFail,
        transientValues:props.transientValues,
        relationships: props.relationships,
        validationSchema: props.validationSchema,
        isNew: isNew,
        loaded:!props.query.loading, // meh
    });
    const blocker = ( tx ) => {
        console.log('discard - checking redirect against',{isRedirectAllowed,id});
        if(props.allowRedirectWhen && props.allowRedirectWhen(tx.location.pathname,id)) {
           setRedirectCallback(null);
            tx.retry();
        } else {
            setRedirectCallback(tx);
        }
        //setIsRedirectAllowed(false);
    };
    // ignore the save state and change URL regardless
    const discardChanges = useCallback(()=>{
        console.log('discard clicked',redirectCallback);
        setIsRedirectAllowed(true);
        redirectCallback.retry();
    },[redirectCallback,setIsRedirectAllowed]);

    // go to the new url
    const returnToEdit = useCallback(()=> {
        console.log('discard - RTE');
        // clear the block and forget it existed
        setRedirectCallback(null);
    },[redirectCallback]);

    // do save, but do not redirect
    const doSave = useCallback(()=> {
        // save and hopefully redirect afterwards
        // TODO: redirect only on successful save
        diaForm.handleSubmit();
        setRedirectCallback(null);
    },[diaForm.handleSubmit,redirectCallback]);
    const confirmation = <DiaPromptConfirmation isActive={!!redirectCallback} discard={discardChanges} returnToEdit={returnToEdit} save={doSave} saveFailed={
       false
    }/>
    console.log('DISCARD - RENDER WITH',isRedirectAllowed,diaForm.isDirty);
    const when = (tx)=>{
        return diaForm.isDirty;
    } 
    //diaForm.isDirty
    useEffect(()=> {
        if(isRedirectAllowed && redirectCallback) {
            redirectCallback.retry();
            setRedirectCallback(null);
            setIsRedirectAllowed(false);
        }
    },[isRedirectAllowed,redirectCallback]);
   useBlocker( blocker, diaForm.isDirty);

    return {form:diaForm,waiting:waiting,blocker,confirmation:confirmation};
}